import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getPractices } from 'api/Practice';
import { duplicateCustomForm } from 'api/CustomForms';

import useDebounceApi from 'lib/hooks/useDebounceApi';

import Modal from 'components/shared/Modal/Modal';
import Select from 'components/shared/Select/Select';
import { showAlert } from 'components/shared/Alert/Alert';

const DuplicateForm = ({ showDuplicateModal, setShowDuplicateModal, formId, formVersion }) => {
  const [selectedPractice, setSelectedPractice] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handlePracticeSearch = async (term, resolve) => {
    if (term) {
      const data = await getPractices(navigate, { limit: 10, searchTerm: term });
      const options = data.practices.map((item) => ({
        value: item.id,
        label: ` ${item.name}`
      }));
      resolve(options);
    } else {
      resolve([]);
    }
  };

  const { debouncedFn, clearCache } = useDebounceApi({
    apiFn: handlePracticeSearch
  });

  const handleLoadOptions = (inputValue) => {
    return new Promise((resolve) => {
      debouncedFn(inputValue, resolve);
    });
  };

  // Clear cache when modal closes
  useEffect(() => {
    if (!showDuplicateModal) {
      clearCache();
    }
  }, [showDuplicateModal, clearCache]);

  const mutateDuplicateForm = useMutation({
    mutationFn: () =>
      duplicateCustomForm(navigate, {
        practiceId: selectedPractice.value,
        formId,
        version: formVersion
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['customForm']);
      const buttons = [
        {
          text: 'View practice',
          onClick: () => navigate(`/admin/practices/${selectedPractice.value}/form-builder`)
        }
      ];
      showAlert({
        title: 'Form duplicated',
        color: 'primary',
        buttons
      });
      setShowDuplicateModal(false);
    }
  });

  return (
    <Modal
      handleOpen={showDuplicateModal}
      handleClose={() => setShowDuplicateModal(false)}
      title="Duplicate form"
      footerActions={{
        primary: {
          text: 'Duplicate',
          onClick: () => mutateDuplicateForm.mutate(),
          isLoading: mutateDuplicateForm.isLoading,
          isDisabled: !selectedPractice || mutateDuplicateForm.isLoading
        }
      }}>
      <div className="ModalBody">
        <Select
          label="Select the practice to duplicate the form into"
          onChange={(v) => setSelectedPractice(v)}
          isAsync
          loadOptions={handleLoadOptions}
          placeholder="Start typing practice name"
          value={selectedPractice}
        />
      </div>
    </Modal>
  );
};

export default DuplicateForm;
